import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAtomValue, useStore } from 'jotai';
import request from 'lib/request';
import type { FormationSlotsQueryResponse } from 'types/api-responses';
import {
  compositeRefreshTriggerAtom,
  geometryRefreshAtom,
  slotValuationRefreshTriggerAtom,
} from '../atoms';
import {
  tcInventoryGunBarrelScopeAtom,
  tcSelectedDevAreaAtom,
} from '../type-curve-filters/atoms';
import { buildFormationSlotsPayload } from '../build-formation-slots-payload';
import { slotTableStateAtoms, builderSectionCrossAtom } from './atoms';
import type { InventorySurface } from './surface';

const FORMATION_SLOTS_PATH = '/queries/development_area_formation_slots';

/**
 * Owns ONE surface's inventory formation-slots fetch, writing
 * `slotTableStateAtoms[surface]`. The Main Page mounts `'main'` (always) and the
 * Unit Builder mounts `'builder'` (only while open); each reads its own copy via
 * `useFormationSlotsQuery`, so a builder edit never touches the main table.
 *
 * Formation-agnostic: the backend scopes by `inventoryFormationPackageIds` + unit
 * and returns every formation's slots, so one call serves the whole surface.
 *
 * Seed reuse: on its first run, if the atom already holds this exact request
 * `signature` settled (deep-copied from the Main surface when the builder opened),
 * the fetch is skipped — the builder shows the copy instantly. Re-fires on
 * `geometryRefreshAtom` (either action restamps the slots on both surfaces).
 *
 * Inactive off an inventory surface (`tcInventoryGunBarrelScopeAtom` null) — the
 * plain Type Curve page keeps fetching inside `useFormationSlotsQuery`.
 *
 * @param {InventorySurface} surface - 'main' or 'builder'.
 * @param {number | null} typeCurveHeaderDetailsId - The selected leaf's
 *   type-curve header details id; `null` while the header is still loading.
 * @param {number | null} structureGridCollectionId - The header's default
 *   structure-grid collection id (passed as `collection_ids`), or `null`.
 */
export function useUnitFormationSlotsFor(
  surface: InventorySurface,
  typeCurveHeaderDetailsId: number | null,
  structureGridCollectionId: number | null
): void {
  const store = useStore();
  const slotsAtom = slotTableStateAtoms[surface];
  const inventoryScope = useAtomValue(tcInventoryGunBarrelScopeAtom);
  const selectedDevArea = useAtomValue(tcSelectedDevAreaAtom);
  const geometryRefresh = useAtomValue(geometryRefreshAtom);
  // A persisted valued/notes change restamps the flag here without dragging in
  // the whole geometry fan-out.
  const valuationRefresh = useAtomValue(slotValuationRefreshTriggerAtom);
  const compositeRefresh = useAtomValue(compositeRefreshTriggerAtom);

  // Clones carry a synthetic negative id no endpoint can resolve, so they read
  // their source's slots — the same substitution the gun barrel makes.
  const developmentAreaId =
    selectedDevArea?.sourceDevelopmentAreaId ??
    selectedDevArea?.developmentAreaId ??
    null;
  const workspaceId = inventoryScope?.inventoryWorkspaceId ?? null;
  const packageIdsKey =
    inventoryScope?.inventoryFormationPackageIds?.join(',') ?? '';
  // Builder only: its live edited section line overrides the selection's
  // cross-section, so the builder slot table re-queries for the new section as the
  // boundary is reshaped. `null` (or main surface) → use the selection's own.
  const builderSectionCross = useAtomValue(builderSectionCrossAtom);
  const crossSection =
    surface === 'builder' && builderSectionCross
      ? builderSectionCross
      : selectedDevArea?.crossSectionEndPoints;
  const crossSectionKey = crossSection ? JSON.stringify(crossSection) : '';

  // Stable signature of everything the fetch scopes on — the same key the seed
  // stores, so the builder's first run can recognise the copied data. Excludes
  // the LTD refresh (that's the refetch dimension, keyed separately below).
  const signature = useMemo(
    () =>
      [
        developmentAreaId,
        typeCurveHeaderDetailsId ?? '',
        workspaceId ?? '',
        packageIdsKey,
        structureGridCollectionId ?? '',
        crossSectionKey,
      ].join('|'),
    [
      developmentAreaId,
      typeCurveHeaderDetailsId,
      workspaceId,
      packageIdsKey,
      structureGridCollectionId,
      crossSectionKey,
    ]
  );

  const abortRef = useRef<AbortController | null>(null);
  const lastKeyRef = useRef<string | null>(null);

  const fetchSlots = useCallback(async () => {
    // Only inventory surfaces are surface-owned; off it, leave the atom null so
    // `useFormationSlotsQuery` fetches per-table as before.
    if (!workspaceId || developmentAreaId === null) {
      store.set(slotsAtom, null);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Keep the previous rows on a refetch (only flip `loading`), so the table
    // updates in place instead of blanking. Blank only on the very first load.
    store.set(slotsAtom, (prev) => {
      return prev
        ? { ...prev, loading: true, error: null }
        : { slots: [], totalCount: 0, loading: true, error: null, signature };
    });

    const payload = buildFormationSlotsPayload({
      developmentAreaId,
      typeCurveHeaderDetailsId: typeCurveHeaderDetailsId ?? undefined,
      inventoryWorkspaceId: workspaceId,
      inventoryFormationPackageIds:
        inventoryScope?.inventoryFormationPackageIds,
      // Formation-agnostic — one call serves every formation for the surface.
      crossSectionEndPoints: crossSection,
      collectionIds:
        structureGridCollectionId !== null
          ? [structureGridCollectionId]
          : undefined,
    });

    try {
      const response = await request.post<FormationSlotsQueryResponse, unknown>(
        FORMATION_SLOTS_PATH,
        { query: payload },
        controller
      );
      if (controller.signal.aborted || response.type === 'abort') return;
      if (response.type === 'fatal') {
        store.set(slotsAtom, {
          slots: [],
          totalCount: 0,
          loading: false,
          error: response.message ?? 'Request failed',
          signature,
        });
        return;
      }
      if (response.type === 'error') {
        store.set(slotsAtom, {
          slots: [],
          totalCount: 0,
          loading: false,
          error: 'Failed to load slots',
          signature,
        });
        return;
      }
      if (response.type !== 'success') return;
      store.set(slotsAtom, {
        slots: response.data.results ?? [],
        totalCount: response.data.totalCount ?? 0,
        loading: false,
        error: null,
        signature,
      });
    } catch {
      if (!controller.signal.aborted) {
        store.set(slotsAtom, {
          slots: [],
          totalCount: 0,
          loading: false,
          error: 'Failed to load slots',
          signature,
        });
      }
    }
    // `signature` covers every scoped value read inside; `crossSection` /
    // `inventoryScope` are read by value through it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, store, slotsAtom]);

  useEffect(() => {
    const key = `${signature}|${geometryRefresh}|${valuationRefresh}|${compositeRefresh}`;
    if (lastKeyRef.current === key) return;
    const firstRun = lastKeyRef.current === null;
    lastKeyRef.current = key;
    // Seed reuse: on the builder's first run the atom may already hold this exact
    // signature settled (deep-copied from main on open) — adopt it, skip the POST.
    if (firstRun) {
      const current = store.get(slotsAtom);
      if (current && current.signature === signature && !current.loading)
        return;
    }
    void fetchSlots();
  }, [
    signature,
    geometryRefresh,
    valuationRefresh,
    compositeRefresh,
    fetchSlots,
    store,
    slotsAtom,
  ]);

  // Abort any in-flight request, and clear this surface's result, on unmount so a
  // stale unit's slots can't leak (the Main surface between selections; the
  // Builder surface is also reset explicitly on close).
  useEffect(
    () => () => {
      abortRef.current?.abort();
      store.set(slotsAtom, null);
    },
    [store, slotsAtom]
  );
}
